import { useState, useMemo } from 'react';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';

import {
  useUpdateRegimeGroup,
  useDeleteRegimeGroup,
  useUpdateRegime,
  useDeleteRegime,
  useCopyRegime,
  useCreateRegimeGroup,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/hooks/use-aid-regimes';

import type {
  SidebarDragData,
  SidebarDragEvent,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-drag';
import { useRegimesSidebarDragActions } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-drag-actions';
import { useRegimesSidebarGroupActions } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-group-actions';
import { useRegimesSidebarLocalState } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-local-state';
import { useRegimesSidebarModalActions } from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/use-regimes-sidebar-modal-actions';
import type {
  GroupWithRegimes,
  ModalState,
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

export {
  getGroupSortableId,
  getRegimeSortableId,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-drag';
export type {
  SidebarDragData,
  SidebarDragEvent,
  SidebarDragType,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-drag';
export type {
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface UseRegimesSidebarActionsParams {
  userId: string;
  modelId: string | number;
  groups: RegimeGroup[];
  regimes: RegimeItem[];
  expandedGroupIds: Record<string | number, boolean>;
}

interface UseRegimesSidebarActionsResult {
  groupsWithRegimes: GroupWithRegimes[];
  groupIds: string[];
  sensors: ReturnType<typeof useSensors>;
  activeDragData: SidebarDragData | null;
  modalState: ModalState | null;
  isModalLoading: boolean;
  hasPendingGroupUpdate: boolean;
  isGroupPending: (groupId: string | number) => boolean;
  isRegimePending: (regimeId: string | number) => boolean;
  handleDragStart: (event: SidebarDragEvent) => void;
  handleDragOver: (event: SidebarDragEvent) => void;
  handleDragEnd: (event: SidebarDragEvent) => void;
  handleDragCancel: () => void;
  handleGroupToggle: (group: RegimeGroup, checked: boolean) => void;
  handleModalConfirm: (arg?: string | number | null) => Promise<void>;
  buildGroupActions: (group: RegimeGroup) => ActionsDropdownOption[];
  buildRegimeActions: (regime: RegimeItem) => ActionsDropdownOption[];
  closeModal: () => void;
}

export const useRegimesSidebarActions = ({
  userId,
  modelId,
  groups,
  regimes,
  expandedGroupIds,
}: UseRegimesSidebarActionsParams): UseRegimesSidebarActionsResult => {
  const [activeDragData, setActiveDragData] = useState<SidebarDragData | null>(
    null
  );

  const updateGroupMut = useUpdateRegimeGroup();
  const deleteGroupMut = useDeleteRegimeGroup();
  const updateRegimeMut = useUpdateRegime();
  const deleteRegimeMut = useDeleteRegime();
  const copyRegimeMut = useCopyRegime();
  const createGroupMut = useCreateRegimeGroup();

  const mutations = useMemo(
    () => ({
      updateGroupMut,
      createGroupMut,
      deleteGroupMut,
      updateRegimeMut,
      deleteRegimeMut,
      copyRegimeMut,
    }),
    [
      updateGroupMut,
      createGroupMut,
      deleteGroupMut,
      updateRegimeMut,
      deleteRegimeMut,
      copyRegimeMut,
    ]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const {
    localGroups,
    setLocalGroups,
    localRegimes,
    setLocalRegimes,
    groupsWithRegimes,
    groupIds,
    hasPendingGroupUpdate,
    pendingGroupUpdateRef,
    pendingGroupIds,
    pendingRegimeIds,
    isGroupPending,
    isRegimePending,
    addPendingGroup,
    removePendingGroup,
    addPendingRegime,
    removePendingRegime,
  } = useRegimesSidebarLocalState({ groups, regimes, activeDragData });

  const { handleGroupReorder, handleGroupToggle } =
    useRegimesSidebarGroupActions({
      userId,
      modelId,
      localGroups,
      setLocalGroups,
      groupsWithRegimes,
      hasPendingGroupUpdate,
      pendingGroupUpdateRef,
      pendingGroupIds,
      addPendingGroup,
      removePendingGroup,
      updateGroupMut,
    });

  const { handleDragStart, handleDragOver, handleDragEnd, handleDragCancel } =
    useRegimesSidebarDragActions({
      userId,
      modelId,
      setActiveDragData,
      localGroups,
      localRegimes,
      setLocalRegimes,
      pendingGroupIds,
      pendingRegimeIds,
      addPendingRegime,
      removePendingRegime,
      handleGroupReorder,
      updateRegimeMut,
      expandedGroupIds,
    });

  const {
    modalState,
    isModalLoading,
    handleModalConfirm,
    buildGroupActions,
    buildRegimeActions,
    closeModal,
  } = useRegimesSidebarModalActions({
    userId,
    modelId,
    groups,
    regimes,
    mutations,
  });

  return {
    groupsWithRegimes,
    groupIds,
    sensors,
    activeDragData,
    modalState,
    isModalLoading,
    hasPendingGroupUpdate,
    isGroupPending,
    isRegimePending,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    handleGroupToggle,
    handleModalConfirm,
    buildGroupActions,
    buildRegimeActions,
    closeModal,
  };
};
